import { formatCurrency, formatKg } from "../../../utils/helpers";

/** Kiểu dữ liệu + class bảng dùng chung cho trang báo cáo xuất bán và modal dịch vụ của 1 khách. */

export interface ServiceRow {
  item_type: number;
  item_id: number;
  item_code: string;
  item_name: string;
  unit_name: string;
  quantity: number;
  amount: number;
  avg_price: number;
  order_count: number;
  share: number;
}

export interface BranchRow {
  branch_id: number;
  branch_name: string;
  order_count: number;
  gross_amount: number;
  discount_amount: number;
  net_amount: number;
  share: number;
}

export interface CustomerRow {
  customer_id: number;
  customer_code: string;
  customer_name: string;
  customer_phone: string;
  order_count: number;
  gross_amount: number;
  discount_amount: number;
  net_amount: number;
  share: number;
}

export interface Summary {
  order_count: number;
  gross_amount: number;
  discount_amount: number;
  net_amount: number;
}

export interface SalesOutputReport {
  summary: Summary;
  services: ServiceRow[];
  by_branch: BranchRow[];
  by_customer: CustomerRow[];
}

export const EMPTY_SUMMARY: Summary = { order_count: 0, gross_amount: 0, discount_amount: 0, net_amount: 0 };

export const formatPercent = (v: number) => `${(Number(v) || 0).toFixed(2)}%`;

export const TABLE_CLASS =
  "min-w-full border-separate border-spacing-0 text-sm [&_tr>*:last-child]:border-r-0 [&_tbody>tr:last-child>td]:border-b-0";
export const THEAD_CLASS = "[&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-gray-100";
export const TH = "border-b border-r border-gray-200 px-3 py-2 text-center font-semibold text-gray-600";
export const TD = "border-b border-r border-gray-200 px-3 py-2";
export const TD_NUM = `${TD} text-right tabular-nums`;
export const TF = "border-t border-r border-gray-200 px-3 py-2 text-right tabular-nums font-bold text-blue-800 bg-blue-50";

/**
 * Bảng dịch vụ: STT · Mã DV · Dịch vụ · ĐVT · Số lượng · Doanh thu · Giá TB · Số đơn · Tỷ trọng, kèm 3 dòng
 * tổng (trước CK → chiết khấu → thuần). Dòng tổng lấy từ `summary` (toàn bộ), không theo ô tìm kiếm.
 */
const ServiceTable: React.FC<{ rows: ServiceRow[]; summary: Summary }> = ({ rows, summary }) => (
  <table className={TABLE_CLASS}>
    <thead className={THEAD_CLASS}>
      <tr>
        <th className={`${TH} w-14`}>STT</th>
        <th className={TH}>Mã DV</th>
        <th className={`${TH} min-w-[220px]`}>Dịch vụ</th>
        <th className={TH}>ĐVT</th>
        <th className={TH}>Số lượng</th>
        <th className={TH}>Doanh thu (đ)</th>
        <th className={TH}>Giá trung bình (đ)</th>
        <th className={TH}>Số đơn</th>
        <th className={TH}>Tỷ trọng</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r, i) => (
        <tr key={`${r.item_type}-${r.item_id}-${r.unit_name}`} className="hover:bg-gray-50">
          <td className={`${TD} text-center text-gray-400`}>{i + 1}</td>
          <td className={`${TD} whitespace-nowrap text-gray-600`}>{r.item_code || "-"}</td>
          <td className={`${TD} font-medium text-gray-900`}>{r.item_name}</td>
          <td className={`${TD} text-center text-gray-600`}>{r.unit_name || "-"}</td>
          <td className={TD_NUM}>{formatKg(r.quantity)}</td>
          <td className={`${TD_NUM} font-semibold text-gray-900`}>{formatCurrency(r.amount)}</td>
          <td className={TD_NUM}>{formatCurrency(r.avg_price)}</td>
          <td className={`${TD} text-center`}>{r.order_count}</td>
          <td className={TD_NUM}>{formatPercent(r.share)}</td>
        </tr>
      ))}
    </tbody>
    <tfoot>
      <tr>
        <td colSpan={5} className={`${TF} border-t-2 border-t-blue-200`}>Tổng trước chiết khấu</td>
        <td className={`${TF} border-t-2 border-t-blue-200`}>{formatCurrency(summary.gross_amount)}</td>
        <td colSpan={3} className={`${TF} border-t-2 border-t-blue-200 border-r-0`}></td>
      </tr>
      <tr>
        <td colSpan={5} className={`${TF} font-semibold text-orange-700`}>Chiết khấu & giảm trừ</td>
        <td className={`${TF} text-orange-700`}>-{formatCurrency(summary.discount_amount)}</td>
        <td colSpan={3} className={`${TF} border-r-0`}></td>
      </tr>
      <tr>
        <td colSpan={5} className={`${TF} text-blue-900`}>Doanh thu thuần</td>
        <td className={`${TF} text-blue-900`}>{formatCurrency(summary.net_amount)}</td>
        <td colSpan={3} className={`${TF} border-r-0`}></td>
      </tr>
    </tfoot>
  </table>
);

export default ServiceTable;
